import type { Locale } from '@mfw/translations';

import { getLocalizedNameByLocale } from '@mfw/components';
import { config } from '@mfw/translations';
import { v4 as uuidv4 } from 'uuid';

import type {
  ApiEpisodeTagTranslation,
  UiEpisodeTranslation,
  UiEpisodeCategories,
  UiCreateEpisode,
  ApiEpisode,
  UiEpisode,
} from '../../schemas/episodes.schema';

import { DEFAULT_CURRENCY } from '../../config/constants';

function getTagLabel(
  locale: Locale,
  translations?: ApiEpisodeTagTranslation[],
): string {
  if (!translations?.length) return '';
  return (
    translations.find((t) => t.locale === locale)?.name ||
    translations.find((t) => t.locale === config.defaultLocale)?.name ||
    translations[0]?.name ||
    ''
  );
}

function mapTranslationsFromApi(episode: ApiEpisode): UiEpisodeTranslation[] {
  return (episode.translations ?? []).map((translation) => ({
    description: translation.description ?? '',
    locale: [
      {
        label: getLocalizedNameByLocale(translation.locale),
        value: translation.locale,
      },
    ],
    name: translation.name,
  }));
}

function mapCategoriesFromApi(
  episode: ApiEpisode,
  locale: Locale,
): UiEpisodeCategories {
  const categories: UiEpisodeCategories = {};
  (episode.categories ?? []).forEach((category) => {
    categories[category.id] = (category.tags ?? []).map((tag) => ({
      label: getTagLabel(locale, tag.translations),
      value: tag.id,
    }));
  });
  return categories;
}

export function mapEpisodeFromApi(
  episode: ApiEpisode,
  locale: Locale,
): UiEpisode {
  return {
    id: episode.id,
    authorId: episode.author?.id,
    previewOnly: episode.previewOnly,
    authorName: episode.author?.name,
    categories: mapCategoriesFromApi(episode, locale),
    coverSignedUrl: episode.coverSignedUrl,
    coverSource: episode.coverSource,
    coverType: episode.coverType,
    createdAt: episode.createdAt,
    documentSignedUrl: episode.documentSignedUrl,
    documentSource: episode.documentSource,
    documentType: episode.documentType,
    isDeleted: episode.isDeleted ?? false,
    isMarketplace:
      episode.configuration?.marketplace?.enabled ??
      episode.isMarketplace ??
      false,
    isOrg: episode.configuration?.isOrg ?? episode.isOrg ?? true,
    language: episode.language,
    marketplaceCurrency:
      episode.configuration?.marketplace?.currency ?? DEFAULT_CURRENCY,
    marketplaceId: episode.configuration?.marketplace?.id ?? null,
    marketplaceInLibrary:
      episode.configuration?.marketplace?.inLibrary ?? false,
    marketplaceRestorable:
      episode.configuration?.marketplace?.restorable ?? false,
    marketplaceLibraryId: episode.configuration?.marketplace?.libraryId ?? null,
    marketplacePrice: episode.configuration?.marketplace?.price ?? 0,
    organizationId: episode.organizationId,
    stats: episode.stats,
    translations: mapTranslationsFromApi(episode),
    type: episode.type ?? (episode.youtubeUrl ? 'video' : 'document'),
    uniqueId: uuidv4(),
    updatedAt: episode.updatedAt,
    youtubeUrl: episode.youtubeUrl,
    youtubeVideoId: episode.youtubeVideoId,
  };
}

export function createEpisodeApiArgs(episode: UiCreateEpisode) {
  const tags = Object.values(episode.categories ?? {}).flatMap((tagList) =>
    (tagList ?? []).map((tag) => tag.value),
  );

  const translations = (episode.translations ?? []).map((translation) => ({
    description: translation.description,
    locale: translation.locale?.[0]?.value,
    name: translation.name,
  }));

  return {
    configuration: {
      isOrg: episode.isOrg ?? true,
      marketplace: {
        currency: episode.marketplaceCurrency ?? DEFAULT_CURRENCY,
        enabled: Boolean(episode.isMarketplace),
        price: episode.marketplacePrice ?? 0,
      },
    },
    coverSource: episode.coverSource ?? null,
    coverType: episode.coverType ?? null,
    documentSource: episode.documentSource ?? null,
    documentType: episode.documentType ?? null,
    language: episode.language,
    tags,
    translations,
    youtubeUrl: episode.youtubeUrl ?? null,
  };
}
